import { onAuthStateChanged, signOut as firebaseSignOut, type User } from 'firebase/auth';
import { onSnapshot } from 'firebase/firestore';
import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { auth } from '@/lib/firebase';
import { userDoc } from '@/lib/firestore/refs';
import { resetLocalSession, takeResetMessage } from '@/lib/auth/reset-session';
import type { Role, UserProfile } from '@shared/types';

export interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  role: Role | null;
  loading: boolean;
  denied: string | null;
}
export interface AuthContextValue extends AuthState {
  signOut: () => Promise<void>;
  clearDenied: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

const DENIED = 'Your account is not authorised for this portal. Contact an administrator.';
const signedOut: AuthState = { user: null, profile: null, role: null, loading: false, denied: null };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => ({
    ...signedOut,
    loading: true,
    denied: takeResetMessage(),
  }));

  useEffect(() => {
    let unsubProfile: (() => void) | null = null;
    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      unsubProfile?.();
      unsubProfile = null;
      if (!user) {
        setState((s) => ({ ...signedOut, denied: s.denied }));
        return;
      }
      let role = ((await user.getIdTokenResult()).claims.role as Role | undefined) ?? null;
      unsubProfile = onSnapshot(
        userDoc(user.uid),
        async (snap) => {
          const profile = snap.exists() ? snap.data() : null;
          if (!profile || !profile.active) {
            await firebaseSignOut(auth);
            setState({ ...signedOut, denied: DENIED });
            return;
          }
          if (profile.role !== role) {
            // claim changed by an admin: refresh the token so rules and UI agree
            role = ((await user.getIdTokenResult(true)).claims.role as Role | undefined) ?? null;
          }
          if (!role) {
            await firebaseSignOut(auth);
            setState({ ...signedOut, denied: DENIED });
            return;
          }
          setState({ user, profile, role, loading: false, denied: null });
        },
        () => resetLocalSession('Could not load your profile. Your session was reset; please sign in again.'),
      );
    });
    return () => {
      unsubAuth();
      unsubProfile?.();
    };
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
  }, []);
  const clearDenied = useCallback(() => setState((s) => ({ ...s, denied: null })), []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signOut, clearDenied }),
    [state, signOut, clearDenied],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
